/** @jsxImportSource @emotion/react */
import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import useAxiosPrivate from "@/api/hooks/useAxiosPrivate";
import BannerMessage, {
  BANNER_MESSAGE_STATUSES
} from "@/components/BannerMessage/BannerMessage";
import useTableItemDetails from "@/components/DetailsSection/hooks/useTableItemDetails";
import { VehicleDetailsDto } from "@/components/DetailsSection/types";
import TextLink from "@/components/TextLink/TextLink";
import { TEXT_LINK_SIZES } from "@/components/TextLink/types";
import { ROUTES } from "@/shared";
import { refreshTableAndUpdateDetails } from "@/shared/functions/functions";
import useResponse from "@/shared/hooks/useResponse";
import { useVehiclesTableStore } from "@/stores/useTableStore";

interface ActivateVehicleBanner {
  detailsData: VehicleDetailsDto;
}

const ActivateVehicleBanner = ({ detailsData }: ActivateVehicleBanner) => {
  const { t } = useTranslation();
  const { putData } = useAxiosPrivate();
  const { getTableItemDetails } = useTableItemDetails();
  const { handleResponse, handleCommonError } = useResponse();

  const params = useVehiclesTableStore(state => state.params);

  const handleActivate = async () => {
    try {
      const { id, imei, number, comment } = detailsData;

      const payload = {
        id,
        imei,
        name: number,
        comment,
        disabled: false
      };

      await putData(ROUTES.Vehicles, payload);

      refreshTableAndUpdateDetails({
        valueInSearchField: params.contains,
        resourceId: id,
        successMessage: t("Alert##Vehicle successfully activated"),
        itemHasOpenedDetails: true,
        getTableItemDetails,
        handleResponse
      });
    } catch (error) {
      handleCommonError(error, t);
    }
  };

  return (
    <BannerMessage
      data-testid="activate-vehicle-banner"
      status={BANNER_MESSAGE_STATUSES.Warning}
      title={t("Details##deactivated vehicle banner##title")}
      description={t("Details##deactivated vehicle banner##description")}
      css={css({ margin: "24px 16px 24px 24px" })}
      externalAction={
        <TextLink
          size={TEXT_LINK_SIZES.Small}
          onClick={handleActivate}
          sx={{ overflowWrap: "break-word" }}
          customStyle={{ paddingTop: "4px" }}
        >
          {t("Details##deactivated vehicle banner##link")}
        </TextLink>
      }
    />
  );
};

export default ActivateVehicleBanner;
